import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { apArchetypesService } from '../services/apArchetypesService';
import { APArchetype } from '../types/apArchetypes';
import { AlertCircle, Save, Edit, X, Trash2, Plus, FileText } from 'lucide-react';
import AddPropertyModal from '../components/AddPropertyModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';

const APArchetypeDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken, setUserAndToken } = useUser();
  const [archetype, setArchetype] = useState<APArchetype | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [descriptionToDelete, setDescriptionToDelete] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await apArchetypesService.getAPArchetype(id, accessToken, setUserAndToken);
        setArchetype(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load archetype');
      } finally {
        setLoading(false);
      }
    };
    if (accessToken && id) fetchDetails();
  }, [id, accessToken, setUserAndToken]);

  const orderToEvaluate = useMemo(() => {
    const entry = archetype?.descriptions.find(d => d.is_active && d.key === 'order_to_evaluate');
    const n = Number(entry?.value ?? 0);
    return Number.isNaN(n) ? undefined : n;
  }, [archetype]);

  // Use a larger document icon to visually match Category details header
  const renderFixedIcon = () => <FileText className="w-10 h-10 text-slate-700" />;

  const badgeColor = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('key') || n.includes('premium') || n.includes('priority')) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (n.includes('regular') || n.includes('standard') || n.includes('normal')) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (n.includes('risk')) return 'bg-red-100 text-red-800 border-red-200';
    if (n.includes('new') || n.includes('pending')) return 'bg-green-100 text-green-800 border-green-200';
    if (n.includes('document') || n.includes('invoice') || n.includes('bill')) return 'bg-purple-100 text-purple-800 border-purple-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const renderValue = (key: string, value: any) => {
    if (Array.isArray(value)) {
      return (
        <div className="space-y-2">
          <ul className="space-y-1">
            {value.map((item, index) => (
              <li key={index} className="flex items-start">
                <span className="text-gray-500 text-sm mr-2">•</span>
                <span className="text-sm text-gray-700">{typeof item === 'object' ? JSON.stringify(item) : String(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    } else if (typeof value === 'object' && value !== null) {
      return (
        <div className="space-y-2">
          <div className="overflow-hidden">
            <table className="min-w-full">
              <tbody>
                {Object.entries(value as Record<string, any>).map(([subKey, subValue]) => (
                  <tr key={subKey} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-2 pr-4 text-sm text-gray-600 font-medium align-top w-1/3">
                      {subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                    </td>
                    <td className="py-2 text-sm text-gray-700 align-top">
                      {typeof subValue === 'object' ? JSON.stringify(subValue) : String(subValue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else {
      return (
        <div className="space-y-1">
          <div className="text-sm text-gray-700">
            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </div>
        </div>
      );
    }
  };

  const startEdit = (key: string, value: any) => {
    setEditingKey(key);
    if (Array.isArray(value)) {
      setEditingDraft([...value]);
    } else if (typeof value === 'object' && value !== null) {
      setEditingDraft({ ...(value as Record<string, any>) });
    } else {
      setEditingDraft(value ?? '');
    }
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingDraft(null);
  };

  const saveEdit = async () => {
    if (!archetype || !editingKey) return;
    try {
      setSaving(true);
      let parsed: any = editingDraft;
      const updated = await apArchetypesService.upsertDescriptions(
        archetype.id,
        [{ id: archetype.descriptions.find(d => d.key === editingKey)?.id, key: editingKey, value: parsed }],
        accessToken,
        setUserAndToken
      );
      setArchetype(updated);
      setEditingKey(null);
      setEditingDraft(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Editing helpers (object/array/primitive)
  const updatePrimitiveDraft = (value: string) => setEditingDraft(value);

  const updateObjectValue = (subKey: string, value: string) => {
    setEditingDraft((prev: any) => ({ ...(prev || {}), [subKey]: value }));
  };

  const updateObjectKey = (oldKey: string, newKey: string) => {
    const convertToApiKey = (displayName: string): string => {
      return displayName
        .toLowerCase()
        .replace(/[^a-z0-9_\s]/g, '')
        .replace(/\s+/g, '_')
        .trim();
    };
    const apiNewKey = convertToApiKey(newKey);
    setEditingDraft((prev: any) => {
      const copy: Record<string, any> = { ...(prev || {}) };
      const val = copy[oldKey];
      delete copy[oldKey];
      copy[apiNewKey] = val;
      return copy;
    });
  };

  const removeObjectKey = (subKey: string) => {
    setEditingDraft((prev: any) => {
      const copy: Record<string, any> = { ...(prev || {}) };
      delete copy[subKey];
      return copy;
    });
  };

  const addObjectKey = () => {
    setEditingDraft((prev: any) => ({ ...(prev || {}), '': '' }));
  };

  const updateArrayValue = (index: number, value: string) => {
    setEditingDraft((prev: any[]) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      arr[index] = value;
      return arr;
    });
  };

  const addArrayItem = () => {
    setEditingDraft((prev: any[]) => (Array.isArray(prev) ? [...prev, ''] : ['']));
  };

  const removeArrayItem = (index: number) => {
    setEditingDraft((prev: any[]) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      return arr.filter((_, i) => i !== index);
    });
  };

  // Removed inline basics editor; evaluation order is shown as a badge in the header

  const handleAddDescription = async (property: { key: string; value: any }) => {
    if (!archetype) return;
    try {
      setSaving(true);
      const updated = await apArchetypesService.upsertDescriptions(
        archetype.id,
        [{ key: property.key, value: property.value }],
        accessToken,
        setUserAndToken
      );
      setArchetype(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add description');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (key: string) => {
    setDescriptionToDelete(key);
    setShowDeleteModal(true);
  };

  const confirmDeleteDescription = async () => {
    if (!archetype || !descriptionToDelete) return;
    try {
      setIsDeleting(true);
      const updated = await apArchetypesService.deleteDescription(
        archetype.id,
        descriptionToDelete,
        accessToken,
        setUserAndToken
      );
      setArchetype(updated);
      if (editingKey === descriptionToDelete) cancelEdit();
      setShowDeleteModal(false);
      setDescriptionToDelete(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading archetype details...</p>
        </div>
      </div>
    );
  }

  if (error || !archetype) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-6 bg-red-50 border border-red-200 rounded-lg shadow-sm">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-red-800 mb-2">Error Loading Archetype</h3>
          <p className="text-red-700">{error || 'Archetype not found'}</p>
          <button
            onClick={() => navigate('/ap-archetypes')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Back to Archetypes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            {renderFixedIcon()}
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Afacad, sans-serif' }}>{archetype.name}</h1>
              <p className="text-gray-600">{archetype.description}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-4 py-2 rounded-full text-sm font-medium border whitespace-nowrap ${badgeColor(archetype.name)}`}>
            Evaluation Order: {orderToEvaluate ?? 'N/A'}
          </span>
          {archetype.is_default && (
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
              Default
            </span>
          )}
        </div>
      </div>

      

      {/* Descriptions */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">Descriptions</h2>
          <button
            onClick={() => setShowAddModal(true)}
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Add Description
          </button>
        </div>
        <div className="p-6 space-y-6">
          {archetype.descriptions
            .filter(d => d.is_active && d.key !== 'order_to_evaluate')
            .map((d) => (
              <div key={d.id} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-slate-900">{d.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
                  <div className="flex items-center space-x-2">
                    {editingKey !== d.key ? (
                      <>
                        <button
                          onClick={() => startEdit(d.key, d.value)}
                          className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(d.key)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                          disabled={isDeleting}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={saveEdit}
                          disabled={saving}
                          className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                          title="Save"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {editingKey === d.key ? (
                  Array.isArray(editingDraft) ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Array Items</span>
                        <button onClick={addArrayItem} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Add Item
                        </button>
                      </div>
                      {editingDraft.map((val: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={String(val ?? '')}
                            onChange={(e) => updateArrayValue(idx, e.target.value)}
                            className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Array item"
                          />
                          <button onClick={() => removeArrayItem(idx)} className="text-red-500 hover:text-red-700 p-1" title="Remove item">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>)
                      )}
                    </div>
                  ) : (typeof editingDraft === 'object' && editingDraft !== null) ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Object Properties</span>
                        <button onClick={addObjectKey} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Add
                        </button>
                      </div>
                      {Object.entries(editingDraft as Record<string, any>).map(([subKey, subVal], index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            defaultValue={subKey}
                            onBlur={(e) => e.target.value !== subKey && updateObjectKey(subKey, e.target.value)}
                            className="w-1/3 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Key"
                          />
                          <input
                            type="text"
                            value={String(subVal ?? '')}
                            onChange={(e) => updateObjectValue(subKey, e.target.value)}
                            className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Value"
                          />
                          <button onClick={() => removeObjectKey(subKey)} className="text-red-500 hover:text-red-700 p-1" title="Remove property">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={String(editingDraft ?? '')}
                      onChange={(e) => updatePrimitiveDraft(e.target.value)}
                      className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="Enter value"
                    />
                  )
                ) : (
                  renderValue(d.key, d.value)
                )}
              </div>
            ))}

          <AddPropertyModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            title="Add New Description"
            submitLabel="Add Description"
            onSave={async (prop) => {
              await handleAddDescription(prop);
              setShowAddModal(false);
            }}
          />

          <ConfirmDeleteModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setDescriptionToDelete(null);
            }}
            onConfirm={confirmDeleteDescription}
            title="Delete Description"
            message="Are you sure you want to delete this description? This action cannot be undone."
            itemName={descriptionToDelete ? descriptionToDelete.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : ''}
            isLoading={isDeleting}
          />
        </div>
      </div>
    </div>
  );
};

export default APArchetypeDetails;


